/**
 * LightboxContext - Global Image Lightbox Provider
 *
 * Uses yet-another-react-lightbox for professional UX:
 * - Zoom (pinch/scroll)
 * - Swipe navigation
 * - Keyboard shortcuts (arrows, escape)
 * - Touch optimized
 * - Accessible (ARIA)
 *
 * Usage:
 *   // In App.tsx - wrap your app:
 *   <LightboxProvider>
 *     <App />
 *   </LightboxProvider>
 *
 *   // In any component:
 *   const { openLightbox } = useLightbox();
 *   openLightbox(['url1', 'url2'], 0);
 *
 *   // Or just use PostCard - it works automatically!
 *   <PostCard post={postData} />
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import { getLargestImageUrl } from "../components/media/utils";

interface LightboxContextValue {
  /** Open the lightbox with an array of image URLs */
  openLightbox: (images: string[], startIndex?: number) => void;
  /** Close the lightbox */
  closeLightbox: () => void;
  /** Whether the lightbox is currently open */
  isOpen: boolean;
}

const LightboxContext = createContext<LightboxContextValue | null>(null);

interface LightboxProviderProps {
  children: ReactNode;
}

export const LightboxProvider: React.FC<LightboxProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [slides, setSlides] = useState<{ src: string }[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = useCallback((imageUrls: string[], startIndex = 0) => {
    // Convert all URLs to their largest versions and format for library
    const formattedSlides = imageUrls.map((url) => ({
      src: getLargestImageUrl(url),
    }));
    setSlides(formattedSlides);
    setCurrentIndex(startIndex);
    setIsOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ openLightbox, closeLightbox, isOpen }),
    [openLightbox, closeLightbox, isOpen]
  );

  return (
    <LightboxContext.Provider value={contextValue}>
      {children}

      {/* Professional lightbox with zoom, swipe, keyboard navigation */}
      <Lightbox
        open={isOpen}
        close={closeLightbox}
        slides={slides}
        index={currentIndex}
        plugins={[Zoom, Counter]}
        controller={{ closeOnBackdropClick: true }}
        carousel={{
          finite: slides.length <= 1,
          preload: 2,
        }}
        animation={{ fade: 250, swipe: 250 }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true,
        }}
        styles={{
          container: {
            backgroundColor: "rgba(0, 0, 0, 0.95)",
          },
          button: {
            filter: "none",
          },
        }}
        render={{
          buttonPrev: slides.length <= 1 ? () => null : undefined,
          buttonNext: slides.length <= 1 ? () => null : undefined,
        }}
      />
    </LightboxContext.Provider>
  );
};

/**
 * Hook to access the global lightbox
 *
 * @example
 * const { openLightbox } = useLightbox();
 *
 * // Open lightbox with images
 * <img onClick={() => openLightbox(['url1.jpg', 'url2.jpg'], 0)} />
 */
export const useLightbox = (): LightboxContextValue => {
  const context = useContext(LightboxContext);
  if (!context) {
    throw new Error("useLightbox must be used within a LightboxProvider");
  }
  return context;
};

/**
 * Optional hook that returns null if outside provider
 * Useful for components that may or may not be within the provider
 */
export const useLightboxOptional = (): LightboxContextValue | null => {
  return useContext(LightboxContext);
};

export default LightboxProvider;
